import { figmaColors } from '@doltech/ui/lib/figma/colors';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ERROR_NOT_SUPPORTED } from '../../question-data-model/constants';
import { DoTestHeaderDesktop } from '../../student-do-test/mobile/DoTest/Reading/Header/DoTestHeaderDesktop';
import { ListeningBottomNavigation } from '../components/BottomNavigation/ListeningBottomNavigation';
import { ReadingBottomNavigation } from '../components/BottomNavigation/ReadingBottomNavigation';
import { useTestResultStore } from '../model/test-result.hook';
import { ListeningTestResult } from './ListeningTestResult';
import { ReadingTestResult } from './ReadingTestResult';

interface TestResultProps extends React.HTMLAttributes<HTMLDivElement> {
  onQuit?: () => void;
}

const BOTTOM_BAR_HEIGHT = 125;
const PADDING_BOTTOM_TWEAK = 200;

const Main = styled.article`
  display: flex;
  flex-direction: column;

  #test-result-container {
    min-height: 0;
    flex: 1;
    #sidebar-left {
      padding: 16px 24px;
      max-height: calc(100vh - ${BOTTOM_BAR_HEIGHT}px);
      overflow: auto;
      padding-bottom: ${PADDING_BOTTOM_TWEAK}px;
      &::-webkit-scrollbar {
        display: none;
      }
    }
    #body-container {
      overflow: auto;
      max-height: calc(100vh - ${BOTTOM_BAR_HEIGHT}px);
      padding: 16px 24px;
      padding-bottom: ${PADDING_BOTTOM_TWEAK}px;
    }
  }

  .bottom-navigation {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    background: ${colorsV2.white100};
    box-shadow: inset 0 1px 0 ${colorsV2.gray10};
  }
  .btn-back {
    cursor: pointer;
    font-size: 16px;
    margin-bottom: 10px;
    .sub-text {
      margin-left: 8px;
      color: ${figmaColors.text1};
    }
    &:hover {
      .sub-text {
        color: ${figmaColors.neutral9};
      }
    }
  }
`;

export const TestResult = observer((props: TestResultProps) => {
  const { onQuit, ...rest } = props;

  const store = useTestResultStore();

  const currentSection = store?.getCurrentTestSection();

  const renderTestResultBySkill = React.useCallback(() => {
    switch (currentSection?.skill) {
      case SkillEnum.READING:
        return (
          <>
            <ReadingTestResult />
            <ReadingBottomNavigation className="bottom-navigation" />
          </>
        );
      case SkillEnum.LISTENING:
        return (
          <>
            <ListeningTestResult />
            <ListeningBottomNavigation className="bottom-navigation" />
          </>
        );
      default:
        return <div>{`${currentSection.skill} ${ERROR_NOT_SUPPORTED}`}</div>;
    }
  }, [currentSection?.skill]);

  const buildHeaderText = () => {
    if (TestTypeEnum.FULL_TEST === store?.testType) {
      return 'IELTS Online Test';
    }
    // PRACTICE_TEST
    if (SkillEnum.READING === currentSection?.skill) {
      return 'IELTS Reading Practice Test';
    }
    // LISTENING
    return 'IELTS Listening Practice Test';
  };

  if (!store.initialed || isNil(currentSection)) return <div />;

  return (
    <Main {...rest}>
      <DoTestHeaderDesktop
        testName={store.name}
        text={buildHeaderText()}
        isViewResult
        showQuitButton={Boolean(onQuit)}
        onQuit={() => {
          onQuit?.();
        }}
      />
      {renderTestResultBySkill()}
    </Main>
  );
});
